import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Ticket, Trophy, Calendar, ShoppingBag, ArrowRight, ChevronRight,
  Shield, Zap, Users, Clock, Star, Quote, MapPin, ClipboardList
} from "lucide-react";
import axios from "axios";
import { API } from "../config/constants";
import SEO from "../components/SEO";
import { useSiteImages } from "../hooks/useSiteImages";

export default function HomePage() {
  const { getImage } = useSiteImages();
  const [reviews, setReviews] = useState([]);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [reviewsRes, eventsRes] = await Promise.all([
          axios.get(`${API}/reviews/featured`),
          axios.get(`${API}/events/featured`),
        ]);
        setReviews(reviewsRes.data);
        setEvents(eventsRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  const services = [
    { icon: <Ticket size={40} />, title: "E-Tickets", description: "Seamless digital ticketing for your track & field events. Mobile-friendly, instant delivery.", link: "/e-tickets", color: "bg-blue-50" },
    { icon: <Trophy size={40} />, title: "Custom Medals", description: "Premium quality custom medals crafted to celebrate athletic achievements.", link: "/medals", color: "bg-amber-50" },
    { icon: <Calendar size={40} />, title: "Events", description: "Coach Miami Presents brings you the Southeast's premier youth track meets.", link: "/events", color: "bg-green-50" },
    { icon: <ShoppingBag size={40} />, title: "Merch Store", description: "Official Lane4 apparel and accessories for athletes and fans.", link: "/merch", color: "bg-purple-50" },
  ];

  const reasons = [
    { icon: <Shield size={32} />, title: "Trusted by 500+", description: "Coaches and event organizers across the Southeast" },
    { icon: <Zap size={32} />, title: "Fast Delivery", description: "Quick turnaround on medals and event setup" },
    { icon: <Users size={32} />, title: "Personal Service", description: "Direct communication every step of the way" },
    { icon: <Clock size={32} />, title: "15+ Years", description: "Experience in track & field event management" },
  ];

  return (
    <div data-testid="home-page">
      <SEO title="" description="Lane4 Sports Services - Premium e-ticketing, custom medals, and event management for track & field. Trusted by 500+ coaches across the Southeast." />
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center bg-[#0F2137] overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{ backgroundImage: `url(${getImage("home_hero")})` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F2137] via-[#0F2137]/80 to-transparent" />
        <div className="container-custom relative z-10 py-32">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-3xl">
            <span className="section-kicker">Lane4 Sports Services</span>
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-black text-white leading-tight mb-6">
              Elevate Your<br /><span className="text-[#D90429]">Track & Field</span><br />Experience
            </h1>
            <p className="text-lg md:text-xl text-gray-300 mb-10 max-w-xl">
              Premium e-ticketing, custom medals, and event management for coaches, athletes, and organizers who demand excellence.
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-4">
              <Link to="/e-tickets" className="btn-primary flex items-center gap-2" data-testid="hero-cta-tickets">Explore Tickets <ArrowRight size={20} /></Link>
              <Link to="/medals" className="btn-outline-white flex items-center gap-2" data-testid="hero-cta-medals">Get Medals <ArrowRight size={20} /></Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Bento Grid */}
      <section className="py-24 bg-white" data-testid="services-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="section-kicker">What We Do</span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0F2137]">Complete Solutions for Track & Field</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <motion.div key={service.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} viewport={{ once: true }}>
                <Link to={service.link} data-testid={`service-card-${service.title.toLowerCase().replace(/\s/g, '-')}`} className={`block p-8 rounded-xl ${service.color} card-hover h-full group`}>
                  <div className="text-[#D90429] mb-4 group-hover:scale-110 transition-transform">{service.icon}</div>
                  <h3 className="text-xl font-bold text-[#0F2137] mb-3">{service.title}</h3>
                  <p className="text-[#495057] mb-4">{service.description}</p>
                  <span className="text-[#D90429] font-semibold flex items-center gap-2 group-hover:gap-3 transition-all">Learn More <ChevronRight size={18} /></span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Event - Dynamic from DB */}
      {events.length > 0 && (
        <section className="py-24 bg-[#F8F9FA]" data-testid="featured-event-section">
          <div className="container-custom">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="section-kicker">Coming Up</span>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0F2137] mb-6">{events[0].name}</h2>
                {events[0].host && <p className="text-xl text-[#495057] mb-6">{events[0].host}</p>}
                <div className="flex flex-wrap gap-6 mb-8">
                  {events[0].date && (
                    <div className="flex items-center gap-2 text-[#0F2137]">
                      <Calendar className="text-[#D90429]" />
                      <span className="font-medium">{new Date(events[0].date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                    </div>
                  )}
                  {events[0].register_url && (
                    <a href={events[0].register_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[#D90429] hover:text-[#B00322] font-semibold transition-colors">
                      <ClipboardList size={20} />Register
                    </a>
                  )}
                </div>
                {events[0].location && (
                  <div className="flex items-center gap-2 text-[#0F2137] mb-8">
                    <MapPin className="text-[#D90429]" />
                    <span className="font-medium">{events[0].location}</span>
                  </div>
                )}
                <Link to="/events" className="btn-primary inline-flex items-center gap-2">View All Events <ArrowRight size={20} /></Link>
              </div>
              <div className="relative">
                {events[0].image_url && (
                  <img src={events[0].image_url} alt={events[0].name} className="rounded-xl shadow-2xl" />
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Why Choose Lane4 */}
      <section className="py-24 bg-white" data-testid="why-choose-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="section-kicker">Why Lane4</span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#0F2137]">The Lane4 Difference</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {reasons.map((reason, index) => (
              <motion.div key={reason.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} viewport={{ once: true }} className="text-center p-6">
                <div className="w-16 h-16 bg-[#D90429]/10 rounded-full flex items-center justify-center mx-auto mb-4 text-[#D90429]">{reason.icon}</div>
                <h3 className="text-xl font-bold text-[#0F2137] mb-2">{reason.title}</h3>
                <p className="text-[#495057]">{reason.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      {reviews.length > 0 && (
        <section className="py-24 bg-[#0F2137]" data-testid="reviews-section">
          <div className="container-custom">
            <div className="text-center mb-16">
              <span className="text-[#D90429] text-sm font-semibold uppercase tracking-widest mb-2 block">Testimonials</span>
              <h2 className="text-4xl md:text-5xl font-bold text-white">What Coaches Are Saying</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviews.slice(0, 6).map((review, index) => (
                <motion.div key={review.id || `home-review-${index}`} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} viewport={{ once: true }} className="bg-white/10 backdrop-blur-sm p-6 rounded-xl" data-testid={`review-card-${index}`}>
                  <div className="flex gap-1 mb-4">{[...Array(review.rating)].map((_, i) => (<Star key={`star-${review.id}-${i}`} className="text-amber-400 fill-amber-400" size={20} />))}</div>
                  <Quote className="text-[#D90429] mb-3" size={24} />
                  <p className="text-gray-300 mb-4 leading-relaxed">{review.text}</p>
                  <div className="border-t border-white/10 pt-4">
                    <p className="font-semibold text-white">{review.name}</p>
                    {review.organization && <p className="text-gray-400 text-sm">{review.organization}</p>}
                  </div>
                </motion.div>
              ))}
            </div>
            <div className="text-center mt-12">
              <Link to="/reviews" className="btn-outline-white inline-flex items-center gap-2">Read All Reviews <ArrowRight size={20} /></Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-24 bg-[#D90429]" data-testid="cta-section">
        <div className="container-custom text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Ready to Elevate Your Event?</h2>
          <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">Whether you need e-tickets, custom medals, or full event support, Lane4 is here to make it happen.</p>
          <Link to="/contact" className="btn-outline-white inline-flex items-center gap-2" data-testid="footer-cta">Get Started Today <ArrowRight size={20} /></Link>
        </div>
      </section>
    </div>
  );
}
